const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const CURRENCY_PATTERN =
  /^\$?(?<dollars>[0-9]*)(?:\.(?<centsTens>[0-9])(?<centsOnes>[0-9])?)?$/;

export const STRIPE_MINIMUM_CHARGE = 50;
export const STRIPE_MAXIMUM_CHARGE = 99999999;

export const MINIMUM_BASE_PRICE_FOR_PERCENTAGE_FEES = 10_00; // $10.00

export interface Fee {
  threshold: number;
  amount: number;
}

export interface PromoCode {
  type: 'percentage' | 'amount';
  amount: number;
}

// includes the currency symbol
export function fromStripeFormat(amountInCents: number): string {
  return formatter.format(amountInCents / 100);
}

// may or may not include currency symbol and thousands separators
export function toStripeFormat(formatted: string): number {
  const cleaned = formatted.replaceAll(/[^0-9.]+/g, '');
  return Math.round(parseFloat(cleaned) * 100);
}

/**
 * Calculates the total amount such that the gym receives the amount provided.
 * Returned value is in cents.
 */
export function getTotalAmount(baseAmountInCents: number, fees: Fee[]): number | null {
  if (baseAmountInCents === 0) return 0;
  const applicationFee = getApplicationFee(baseAmountInCents, fees);
  if (applicationFee == null) return null;
  return baseAmountInCents + applicationFee;
}

/**
 * Finds the appropriate fee for a given charge based on the thresholds. A fee
 * applies if its threshold is the highest threshold that is smaller than the
 * charge. In other words, this function returns `fees[n].amount` for some `n`
 * such that
 *
 *     fees[n].threshold < baseAmountInCents <= fees[n+1].threshold
 *
 * if `fees` were sorted in ascending order by `threshold`. (The
 * implementation does not assume the `fees` parameter is sorted.)
 */
export function getApplicationFee(baseAmountInCents: number, fees: Fee[]): number | null {
  if (baseAmountInCents === 0) return 0;

  let result: Fee | undefined;

  for (const fee of fees) {
    if (
      baseAmountInCents > fee.threshold &&
      (result === undefined || fee.threshold > result.threshold)
    ) {
      result = fee;
    }
  }
  return result?.amount ?? null;
}

/**
 * Gets the application fee portion of a charge
 */
export function getPercentageFee(baseAmountInCents: number, percentage: number): number {
  if (baseAmountInCents < 0) {
    throw new Error('Base charge is negative');
  }
  if (baseAmountInCents > 0 && baseAmountInCents < STRIPE_MINIMUM_CHARGE) {
    throw new Error('Base charge is below minimum');
  }
  if (percentage < 0 || percentage > 100) {
    throw new Error('Invalid percentage (must be between 0 and 100 inclusive)');
  }

  return Math.ceil((baseAmountInCents * percentage) / 100);
}

export function getTotalWithPercentageFee(baseAmountInCents: number, percentage: number): number {
  return baseAmountInCents + getPercentageFee(baseAmountInCents, percentage);
}

// returns a string that can be used in a form field
export function centsToFormValue(amountInCents: number | null) {
  return amountInCents !== null ? (amountInCents / 100).toFixed(2) : null;
}

const parseIntOrZero = (value: string | null) =>
  value != null && value !== '' ? parseInt(value, 10) : 0;

export function centsFromFormValue(value: string | undefined) {
  if (value == null) return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  const match = trimmed.match(CURRENCY_PATTERN);
  if (!match?.groups) return null;

  const { dollars, centsTens, centsOnes } = match.groups;
  const parsedDollars = parseIntOrZero(dollars);
  const parsedCentsTens = parseIntOrZero(centsTens);
  const parsedCentsOnes = parseIntOrZero(centsOnes);
  const totalCents = parsedDollars * 100 + parsedCentsTens * 10 + parsedCentsOnes;

  return totalCents;
}

export function getAmountWithPromoCode(
  baseAmountInCents: number,
  promoCode?: PromoCode,
  minimumAmount = MINIMUM_BASE_PRICE_FOR_PERCENTAGE_FEES,
): { priceCentsWithDiscount: number; isBelowMinimum: boolean } {
  if (!promoCode) return { priceCentsWithDiscount: baseAmountInCents, isBelowMinimum: false };

  let discountedAmountInCents = baseAmountInCents;
  if (promoCode.type === 'percentage') {
    const remainingPercentage = 100 - promoCode.amount;
    discountedAmountInCents = Math.ceil(baseAmountInCents * (remainingPercentage / 100));
  } else if (promoCode.type === 'amount') {
    discountedAmountInCents = baseAmountInCents - promoCode.amount;
  } else {
    throw new Error('Invalid promo code type');
  }

  // If a code reduces the price to 0 or below, the price is 0.
  if (discountedAmountInCents <= 0) return { priceCentsWithDiscount: 0, isBelowMinimum: false };

  // Otherwise, ensure it's at least a minimum amount.
  return {
    priceCentsWithDiscount: Math.max(discountedAmountInCents, minimumAmount),
    isBelowMinimum:
      discountedAmountInCents < minimumAmount &&
      Math.abs(discountedAmountInCents - minimumAmount) > Number.EPSILON,
  };
}
